import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["wysiwyg", "backgroundInput"]
  static values = {
    fallbackColor: String,
    fallbackStack: Array,
  }

  connect() {
    this.bindJscolorHooks()
    this.updateEditors()
    this.updateColorSwatchSelection()
    this.element.addEventListener('change', (event) => {
      if (event.target.name === 'presentation_widget[widget_attributes][color]') {
        this.updateColorSwatchSelection()
      }
    })
  }

  updateColorSwatchSelection() {
    const swatches = this.element.querySelectorAll('.widget-button-form__color-swatch')
    swatches.forEach((swatch) => {
      const input = document.getElementById(swatch.getAttribute('for'))
      if (input && input.checked) {
        swatch.style.boxShadow = '0 0 0 2px #212529'
        swatch.style.transform = 'scale(1.15)'
      } else {
        swatch.style.boxShadow = '0 0 0 1px #dee2e6'
        swatch.style.transform = ''
      }
    })
  }

  backgroundChanged() {
    this.updateEditors()
  }

  clearBackgroundImage(event) {
    event.preventDefault()
    const form = this.element
    const fileSelect = form.querySelector('select[name="presentation_widget[background_project_file_id]"]')
    const repeatSelect = form.querySelector('select[name="presentation_widget[background_repeat]"]')
    const sizeSelect = form.querySelector('select[name="presentation_widget[background_size]"]')
    const positionSelect = form.querySelector('select[name="presentation_widget[background_position]"]')

    if (fileSelect) fileSelect.value = ''
    if (repeatSelect) repeatSelect.value = ''
    if (sizeSelect) sizeSelect.value = ''
    if (positionSelect) positionSelect.value = ''

    event.currentTarget.remove()
  }

  updateEditors() {
    const fallbackColor = this.resolveFallbackColor()
    const pickerColor = this.resolvePickerColor(fallbackColor)
    const finalFallback = fallbackColor || ""
    const finalPicker = pickerColor || finalFallback

    this.wysiwygTargets.forEach((element) => {
      element.dataset.wysiwygBackgroundColor = finalFallback
      element.dataset.wysiwygPickerColor = finalPicker
      element.dispatchEvent(
        new CustomEvent("wysiwyg:background-change", {
          detail: { color: finalPicker, fallbackColor: finalFallback },
          bubbles: false,
        })
      )
    })
  }

  resolveFallbackColor() {
    const stack = this.localFallbackStack()
    const bodyColor = this.bodyBackgroundColor()

    if (stack.length === 0) {
      return bodyColor || ""
    }

    return this.compositeColorStack(stack, bodyColor)
  }

  resolvePickerColor(fallbackColor) {
    const explicit = this.explicitColor()
    return explicit || fallbackColor
  }

  explicitColor() {
    const input = this.backgroundInputTargets.find(
      (element) => element.value && element.value.trim().length > 0
    )

    return input ? input.value.trim() : null
  }

  localFallbackStack() {
    if (this.hasFallbackStackValue) {
      return this.fallbackStackValue.filter((color) => color && color.length > 0)
    }

    if (this.hasFallbackColorValue && this.fallbackColorValue) {
      return [this.fallbackColorValue]
    }

    return []
  }

  bindJscolorHooks() {
    this.backgroundInputTargets.forEach((input) => this.ensureJscolorHook(input))
  }

  ensureJscolorHook(input, attempt = 0) {
    if (input.dataset.jscolorHooked === "true") {
      return
    }

    const picker = input.jscolor

    if (!picker) {
      if (attempt > 10) {
        return
      }

      requestAnimationFrame(() => this.ensureJscolorHook(input, attempt + 1))
      return
    }

    const originalSetValueElementValue = picker.setValueElementValue.bind(picker)

    picker.setValueElementValue = (value) => {
      const previousValue = input.value
      originalSetValueElementValue(value)

      if (previousValue !== input.value) {
        input.dispatchEvent(new Event("input", { bubbles: true }))
        input.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    input.dataset.jscolorHooked = "true"
  }

  compositeColorStack(stack, baseColor) {
    let result = this.parseColor(baseColor) || this.transparentColor()

    for (let index = stack.length - 1; index >= 0; index -= 1) {
      const layer = this.parseColor(stack[index])
      if (!layer) {
        continue
      }

      result = this.composite(layer, result)
    }

    return this.colorToString(result)
  }

  composite(top, bottom) {
    const topAlpha = top.a
    const bottomAlpha = bottom.a
    const outAlpha = topAlpha + bottomAlpha * (1 - topAlpha)

    if (outAlpha === 0) {
      return this.transparentColor()
    }

    const blend = (componentTop, componentBottom) => {
      return (
        (componentTop * topAlpha + componentBottom * bottomAlpha * (1 - topAlpha)) /
        outAlpha
      )
    }

    return {
      r: blend(top.r, bottom.r),
      g: blend(top.g, bottom.g),
      b: blend(top.b, bottom.b),
      a: outAlpha,
    }
  }

  parseColor(value) {
    if (!value) {
      return null
    }

    const trimmed = value.trim()

    if (trimmed.startsWith("rgba") || trimmed.startsWith("rgb")) {
      const components = trimmed
        .replace(/rgba?\(/i, "")
        .replace(")", "")
        .split(",")
        .map((component) => component.trim())

      const r = parseFloat(components[0])
      const g = parseFloat(components[1])
      const b = parseFloat(components[2])
      const a = components.length > 3 ? parseFloat(components[3]) : 1

      if ([r, g, b, a].some((component) => isNaN(component))) {
        return null
      }

      return { r, g, b, a: Math.max(0, Math.min(1, a)) }
    }

    if (trimmed.startsWith("#")) {
      const hex = trimmed.slice(1)
      const normalized = hex.length === 3 ? hex.replace(/./g, (char) => char + char) : hex

      if (normalized.length !== 6) {
        return null
      }

      const r = parseInt(normalized.slice(0, 2), 16)
      const g = parseInt(normalized.slice(2, 4), 16)
      const b = parseInt(normalized.slice(4, 6), 16)

      if ([r, g, b].some((component) => isNaN(component))) {
        return null
      }

      return { r, g, b, a: 1 }
    }

    return null
  }

  colorToString(color) {
    const r = Math.round(Math.max(0, Math.min(255, color.r)))
    const g = Math.round(Math.max(0, Math.min(255, color.g)))
    const b = Math.round(Math.max(0, Math.min(255, color.b)))
    const a = Math.max(0, Math.min(1, Number(color.a.toFixed(3))))

    return `rgba(${r}, ${g}, ${b}, ${a})`
  }

  transparentColor() {
    return { r: 0, g: 0, b: 0, a: 0 }
  }

  bodyBackgroundColor() {
    if (!document.body) {
      return null
    }

    const styles = getComputedStyle(document.body)
    const cssVarColor = styles.getPropertyValue("--body-bg").trim()

    if (cssVarColor) {
      return cssVarColor
    }

    const backgroundColor = styles.backgroundColor

    if (backgroundColor && backgroundColor !== "rgba(0, 0, 0, 0)" && backgroundColor !== "transparent") {
      return backgroundColor
    }

    return null
  }
}
